import { useRef } from 'react';
import type { ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { V2Tabs, V2Button, Icon, type SakhtarIconName } from '@sakhtar/design-system/react';
import { tenantBasePath } from '../../lib/tenant.js';
import { computeActiveTab } from './tabMatch.js';

export interface ModuleTab {
  readonly id: string;
  /** Relative to the module base — `''` for the index/overview route. */
  readonly hrefSuffix: string;
  readonly label: string;
}

export interface ModuleCta {
  readonly label: string;
  readonly icon: SakhtarIconName;
  /** Relative to the module base, e.g. `/opportunities`. */
  readonly hrefSuffix: string;
  /** Tab ids this CTA shows on; omit to show on every tab. */
  readonly tabIds?: readonly string[];
}

export interface ModuleLayoutProps {
  /** Path segment under `/t/:slug`, e.g. `'crm'`. */
  readonly moduleId: string;
  readonly title: string;
  readonly subtitle?: string;
  readonly tabs: readonly ModuleTab[];
  readonly cta?: ModuleCta;
  /** Rendered between the module header and the tab strip (e.g. the finance book switcher). */
  readonly toolbar?: ReactNode;
}

/**
 * Persistent module shell: title + subtitle + tabs + an optional primary
 * CTA that stay mounted across every subroute of a module, with an
 * `<Outlet/>` for the active subpage. Fixes the reported "module header
 * disappears on subpages" gap — previously each page (CrmHubPage,
 * LeadsPage, OpportunitiesPage, …) rendered its own one-off title block
 * with no shared tabs, so navigating into a module lost all module
 * context. One `ModuleLayout` instance per module (CrmLayout.tsx et al.,
 * in this same directory) now owns that instead.
 *
 * Tab matching is prefix-based (see `computeActiveTab`), so a detail route
 * under a tab (e.g. `/crm/quotes/:ref`) still highlights that tab rather
 * than falling back to the module's overview tab.
 */
/**
 * v2 module mark per module id (Sakhtar Cloud ERP v2 module headers). The
 * mark's color/pattern comes from `[data-module]` (design-system
 * components.css), so the same id drives every tinted card header and table
 * inside the module — the Finance frame's language, applied per module.
 */
const MODULE_ICON: Record<string, SakhtarIconName> = {
  crm: 'sales',
  inventory: 'inventory',
  procurement: 'cart',
  finance: 'finance',
  projects: 'projects',
  'master-data': 'box',
};

export function ModuleLayout({ moduleId, title, subtitle, tabs, cta, toolbar }: ModuleLayoutProps) {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  // The module's own entrance already animates the whole layout; the page
  // wrapper below only plays its entrance once the user has switched tabs.
  const shownTab = useRef<string | undefined>(undefined);
  const tabSwitches = useRef(0);

  if (!slug) return null;

  const base = `${tenantBasePath(slug)}/${moduleId}`;
  // A user can hold no key for any tab of this module (navAccess.ts hides them
  // all) yet still reach its URL directly; the page's own 403 panel handles that.
  const activeTab = tabs.length > 0 ? computeActiveTab(location.pathname, base, tabs) : undefined;
  if (shownTab.current !== undefined && shownTab.current !== activeTab?.id)
    tabSwitches.current += 1;
  shownTab.current = activeTab?.id ?? '';
  const showCta = cta && activeTab && (!cta.tabIds || cta.tabIds.includes(activeTab.id));

  return (
    <div
      className="sk-module-root"
      data-module={moduleId}
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div className="sk-module-head">
          <span className="sk-module-mark" aria-hidden="true">
            <Icon name={MODULE_ICON[moduleId] ?? 'layers'} size="md" />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <h1 className="sk-module-head__title">{title}</h1>
            {subtitle ? <div className="sk-module-head__subtitle">{subtitle}</div> : null}
          </div>
        </div>
        {showCta ? (
          <Link to={`${base}${cta.hrefSuffix}`} style={{ textDecoration: 'none' }}>
            <V2Button variant="primary" icon={cta.icon}>
              {cta.label}
            </V2Button>
          </Link>
        ) : null}
      </div>

      {toolbar}

      {activeTab ? (
        <V2Tabs
          activeId={activeTab.id}
          items={tabs.map((t) => ({ id: t.id, label: t.label }))}
          onChange={(id) => {
            const tab = tabs.find((t) => t.id === id);
            if (tab) navigate(`${base}${tab.hrefSuffix}`);
          }}
        />
      ) : null}

      {/* Keyed by tab so a tab change replays the page entrance (pageEnter.css). */}
      <div
        className={
          tabSwitches.current > 0 ? 'sk-module-page sk-module-page--enter' : 'sk-module-page'
        }
        key={activeTab?.id ?? 'none'}
      >
        <Outlet />
      </div>
    </div>
  );
}
